import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { useEstudioId } from '../UserContext';

const initials = (nombre, email) => ((nombre || email || '?').substring(0, 2).toUpperCase());

export default function GestionUsuarios() {
  const estudioId = useEstudioId();
  const [miembros, setMiembros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailInvite, setEmailInvite] = useState('');
  const [invitando, setInvitando] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [miId, setMiId] = useState(null);

  const showToast = (msg, tipo = 'ok') => {
    setToast({ msg, tipo });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchMiembros = useCallback(async () => {
    if (!estudioId) return;
    setLoading(true);
    const { data } = await supabase
      .from('estudio_miembros')
      .select('id, rol, activo, user_id, profiles(nombre, email)')
      .eq('estudio_id', estudioId)
      .order('creado_en', { ascending: true });
    setMiembros(data || []);
    setLoading(false);
  }, [estudioId]);

  useEffect(() => {
    fetchMiembros();
    supabase.auth.getSession().then(({ data: { session } }) => setMiId(session?.user?.id));
  }, [fetchMiembros]);

  async function cambiarRol(miembroId, nuevoRol) {
    const { error } = await supabase.from('estudio_miembros').update({ rol: nuevoRol }).eq('id', miembroId);
    if (error) { showToast('Error al cambiar rol', 'error'); return; }
    showToast('Rol actualizado ✓');
    fetchMiembros();
  }

  async function toggleActivo(miembro) {
    const nuevoEstado = !miembro.activo;
    const { error } = await supabase.from('estudio_miembros').update({ activo: nuevoEstado }).eq('id', miembro.id);
    if (error) { showToast('Error al actualizar acceso', 'error'); return; }
    showToast(nuevoEstado ? 'Acceso reactivado ✓' : 'Acceso revocado');
    fetchMiembros();
  }

  async function invitar() {
    const email = emailInvite.trim().toLowerCase();
    if (!email || !email.includes('@')) { showToast('Ingresá un email válido', 'error'); return; }
    setInvitando(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true, data: { estudio_id: estudioId } },
    });
    setInvitando(false);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    showToast(`Invitación enviada a ${email} ✓`);
    setEmailInvite('');
  }

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '32px 24px' }}>

      <div style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text4)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Sistema</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Gestión de usuarios</div>
        <div style={{ fontSize: 13, color: 'var(--text4)' }}>Administrá los accesos al estudio. Solo el admin puede ver esta sección.</div>
      </div>

      {/* Invitar */}
      <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '18px 20px', marginBottom: 28 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 12 }}>Invitar nuevo usuario</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            type="email"
            className="form-input"
            placeholder="email@ejemplo.com"
            value={emailInvite}
            onChange={e => setEmailInvite(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && invitar()}
            style={{ flex: 1 }}
          />
          <button className="btn btn-primary" onClick={invitar} disabled={invitando || !emailInvite.trim()}>
            {invitando ? 'Enviando…' : 'Enviar invitación'}
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 8 }}>
          El usuario recibirá un email con un enlace para ingresar. Se creará con rol <strong>Colaborador</strong> por defecto.
        </div>
      </div>

      {/* Lista */}
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
        Usuarios con acceso
        <span style={{ marginLeft: 8, fontWeight: 400, color: 'var(--text4)' }}>{miembros.length}</span>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)', fontSize: 13 }}>Cargando…</div>
      ) : miembros.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text4)', fontSize: 13 }}>No hay usuarios registrados.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {miembros.map(m => {
            const esMio   = m.user_id === miId;
            const inactivo = m.activo === false;
            const nombre  = m.profiles?.nombre;
            const email   = m.profiles?.email;
            return (
              <div
                key={m.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  background: inactivo ? 'var(--surface2)' : 'var(--surface)',
                  border: '0.5px solid var(--border)',
                  borderRadius: 10, padding: '12px 16px',
                  opacity: inactivo ? 0.65 : 1,
                }}
              >
                <div style={{
                  width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                  background: m.rol === 'admin' ? 'var(--primary)' : '#e2e8f0',
                  color: m.rol === 'admin' ? 'white' : '#475569',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700,
                }}>
                  {initials(nombre, email)}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                      {nombre || email || 'Sin nombre'}
                    </span>
                    {esMio && (
                      <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--primary)', background: '#eff6ff', border: '0.5px solid #bfdbfe', borderRadius: 6, padding: '1px 6px' }}>Vos</span>
                    )}
                    {inactivo && (
                      <span style={{ fontSize: 10, fontWeight: 600, color: '#dc2626', background: '#fef2f2', border: '0.5px solid #fecaca', borderRadius: 6, padding: '1px 6px' }}>Sin acceso</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 2 }}>{email || 'Sin email registrado'}</div>
                </div>

                {esMio ? (
                  <span style={{ fontSize: 12, color: 'var(--text3)', fontWeight: 600, padding: '5px 10px', background: 'var(--surface2)', borderRadius: 6, flexShrink: 0 }}>
                    {m.rol === 'admin' ? 'Admin' : 'Colaborador'}
                  </span>
                ) : (
                  <select
                    className="form-select"
                    value={m.rol}
                    onChange={e => cambiarRol(m.id, e.target.value)}
                    style={{ fontSize: 12, padding: '5px 10px', width: 'auto', flexShrink: 0 }}
                  >
                    <option value="colaborador">Colaborador</option>
                    <option value="admin">Admin</option>
                  </select>
                )}

                {!esMio && (
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: inactivo ? '#16a34a' : '#dc2626' }}
                    onClick={() => {
                      if (!inactivo) {
                        setConfirm({
                          mensaje: `¿Revocar el acceso de ${nombre || email}? No podrá ingresar hasta que lo reactives.`,
                          onConfirmar: () => { setConfirm(null); toggleActivo(m); },
                        });
                      } else {
                        toggleActivo(m);
                      }
                    }}
                  >
                    {inactivo ? 'Reactivar' : 'Revocar acceso'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {confirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 12, padding: '28px 28px 22px', maxWidth: 360, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Confirmar acción</div>
            <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 22 }}>{confirm.mensaje}</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setConfirm(null)}>Cancelar</button>
              <button className="btn" style={{ background: '#dc2626', color: 'white', border: 'none' }} onClick={confirm.onConfirmar}>Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: toast.tipo === 'error' ? '#fef2f2' : '#f0fdf4', color: toast.tipo === 'error' ? '#b91c1c' : '#15803d', border: `0.5px solid ${toast.tipo === 'error' ? '#fecaca' : '#bbf7d0'}`, padding: '8px 18px', borderRadius: 8, fontSize: 13, fontWeight: 500, zIndex: 999, boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
